/**
 * Bill PDF download — in a browser AND inside the native app.
 *
 * In a browser the PDF is fetched with the login token and saved through a
 * temporary blob link. The native app's WebView has no download handling, so
 * that same click does nothing there: no file, no error (reported from
 * Fructose India, Oct 2026 — "PDF" in My Bills did not open anything).
 *
 * Inside the app we instead ask the API for a short-lived link to that one
 * bill and navigate to it. The link is on the API host, not the host the app
 * was loaded from, and Capacitor hands any top-level navigation to another
 * host over to the device (Android: the browser / PDF viewer via an intent;
 * iOS: Safari) while the app itself stays on the page it was on — the same
 * way the WhatsApp and store links already leave the app.
 */
import api from "./api";
import { BACKEND_URL } from "./backendUrl";

export const isNativeApp = (win = typeof window !== "undefined" ? window : undefined) =>
  !!win?.Capacitor?.isNativePlatform?.();

/**
 * Hand `url` to the device from inside the native app. A URL on another host
 * is navigated to directly (not subject to popup blocking, and intercepted by
 * the shell before the page changes). A URL on the app's own host would
 * replace the app if navigated to, so that case opens a new window instead.
 */
export function openOutsideApp(url, win = window) {
  let sameHost = true;
  try {
    sameHost = new URL(url, win.location.href).host === win.location.host;
  } catch {
    /* unparseable → treat as our own host, never navigate the app away */
  }
  if (sameHost) {
    win.open(url, "_blank");
    return "window";
  }
  win.location.assign(url);
  return "navigate";
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * @param {string} pdfPath   API path that streams the PDF (login header required)
 * @param {string} linkPath  API path that returns `{url}` — the short-lived link
 * @param {object} linkBody  body for `linkPath`
 * @param {string} filename  name for the saved file (browser path)
 * @returns {Promise<"opened"|"saved">}
 */
export async function downloadBillPdf({ pdfPath, linkPath, linkBody, filename }) {
  if (isNativeApp()) {
    const { data } = await api.post(linkPath, linkBody || {});
    openOutsideApp(`${BACKEND_URL}${data.url}`);
    return "opened";
  }
  const { data } = await api.get(pdfPath, { responseType: "blob" });
  saveBlob(data, filename);
  return "saved";
}
